import React, { memo, useContext, useCallback, Fragment } from 'react';
import { Skeleton } from '@material-ui/lab';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  INNER_HEIGHT,
  MIME_TYPE_COLLECTION as MimeTypeCollection,
  FILE_ICON_COLLECTION as FileIconCollection,
  TextFileIcon,
} from '../../../constants/patientChart.constant';
import { formattedDate } from '../../../utils/time';
import Modal, { modalHeight, modalWidth } from '../../subComponents/commonModal/v3';
import PPButton from '../../subComponents/button';
import { PDFViewer } from "../../PDFViewer";

const Embed = ({
  title = '',
  open = false,
  document = { key: '', type: '', src: '' },
  height,
  onClose: handleCloseFile = () => {},
}) => {
  const mimeType = MimeTypeCollection.get(document.type);
  const src = `data:${mimeType};base64,${document.src}`;

  const PDFUICollection = new Map([
    [true, PDFViewer],
    [false, Fragment],
  ]);

  const PDFUIPropsCollection = new Map([
    [true, {
      file: src,
      style: { minWidth: 760, height },
    }],
    [false, {}],
  ]);

  const PDF = PDFUICollection.get(open);
  const pdfProps = PDFUIPropsCollection.get(open);

  return (
    <Modal
      title={title}
      open={open}
      style={{
        maxHeight: modalHeight.s2 - 4,
        minWidth: modalWidth.m,
        width: modalWidth.l + 32,
      }}
    >
      <div className='flex flex-column flex-fill m-5 patient-chart-container' dataId={document.key}>
        <div className='flex flex-fill flex-column align-items-center m-b-5'>
          {
            document.type === 'pdf'
              ? <PDF { ...pdfProps } />
              : <embed type={mimeType} src={src} className='max-width-full' />
          }
        </div>

        <div className='flex justify-content-flex-end'>
          <PPButton
            label='Close'
            variant='cancel'
            onClick={handleCloseFile.bind(this, { key: document.key })}
          />
        </div>
      </div>
    </Modal>
  );
};

const UploadsHeading = () => (
  <div
    className={`
      flex align-items-center
      justify-content-flex-start
      height-38
      p-l-3
      p-r-3
      bg-lighter-grey
    `}
  >
    <div className='flex align-items-center'>
      <div className='min-width-110 font-roboto font-14 m-r-6'>Date</div>
      <div className='min-width-165 font-roboto font-14 m-r-6'>Order ID</div>
    </div>

    <div className='flex flex-fill align-items-center justify-content-space-between'>
      <div
        className='flex align-items-center pointer'
      >
        <div className='font-roboto font-14'>Document Type</div>
      </div>
    </div>
  </div>
);

export const Uploads = memo(({
  tabKey = '',
  selectedKey = '',
  data: {
    patientUploads = {},
  }
}) => {
  const {
    ModalCollection,
    handleModalOpen,
    handleModalClose,
  } = useContext(PatientChartContext);
  const { api: { data: uploads = [], loading = false, called = false } } = patientUploads;

  const backgroundClass = useCallback((index = 0) => {
    let className = '';
    if (index % 2 === 0) {
      className = 'bg-lightest-primary';
    }
    return className;
  }, []);

  const Loading = (
    <div className='flex flex-column flex-fill'>
      <UploadsHeading />
      <div className={`
        flex
        align-items-center
        justify-content-space-between
        height-38
        p-l-3
        p-r-3
        ${backgroundClass(0)}
      `}>
        <div className='flex align-items-center'>
          <Skeleton variant='text' className='width-110 m-r-6' />
          <Skeleton variant='text' className='width-165 m-r-6' />
        </div>
        <div className='flex flex-fill align-items-center justify-content-space-between'>
          <Skeleton variant='text' className='width-165' />
        </div>
      </div>
    </div>
  );

  const Uploads = (
    <div className='flex flex-column flex-fill'>
      <UploadsHeading />
      {
        uploads.map(({ uniqueId = '', DocumentId = '', UploadName = '', UploadType = '', OrderId: orderId = '', CreatedAt = '', imageType = 'txt', base64Image = '' }, index) => (
          <div
            key={uniqueId}
            dataId={uniqueId}
            className={`
              flex align-items-center
              justify-content-flex-start
              height-38
              p-l-3
              p-r-3
              ${backgroundClass(index)}
            `}
          >
            <Embed
              title={`${UploadType}.${imageType}`}
              open={ModalCollection.has(uniqueId)}
              document={{ key: uniqueId, src: base64Image, type: imageType }}
              height={INNER_HEIGHT}
              onClose={handleModalClose}
            />

            <div className='flex align-items-center'>
              <div className='upload-date bold min-width-110 font-roboto font-14 m-r-6'>{formattedDate(CreatedAt)}</div>
              <div className='upload-order-id min-width-165 font-roboto font-14 m-r-6'>{orderId}</div>
            </div>
    
            <div className='flex flex-fill align-items-center justify-content-space-between'>
              <div
                className='flex align-items-center pointer'
                onClick={handleModalOpen.bind(this, {
                  key: uniqueId,
                  orderId,
                  fileName: UploadName,
                  type: imageType,
                  label: UploadType,
                  base64: base64Image,
                })}
              >
                <img className='height-20 m-r-1 upload-icon' src={FileIconCollection.get(imageType) || TextFileIcon} alt='file_icon' />
                <div className='upload-name font-roboto font-14 bold'>{UploadType}</div>
              </div>
            </div>
          </div>
        ))
      }
    </div>
  );

  const NoUploads = (
    <div className='flex flex-column flex-fill'>
      <UploadsHeading />
      <div className='flex align-items-center justify-content-flex-start in-progress height-40 font-roboto font-14 p-l-3 p-r-3'>No upload to be displayed.</div>
    </div>
  );

  const Component = new Map([
    ['true-false-true', Uploads],
    ['true-false-false', NoUploads],
  ]);

  const displayClass = tabKey !== selectedKey ? 'd-none' : '';

  return (
    <div className={`flex flex-column flex-fill column-content scroll-y-auto ${displayClass}`}>
      {Component.get(`${called}-${loading}-${!!uploads.length}`) || Loading}
    </div>
  );
});

export default Uploads;
